import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import Particles from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { DURATION, EASE, SPRING, STAGGER } from '../../animations/timing';
import { useMagneticEffect } from '../../animations/CustomCursor';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import { FaWhatsapp } from 'react-icons/fa';

// ─── Headline words (split for stagger)
const HEADLINE = ['Build', 'Ideas', 'That', 'Actually', 'Work.'];
const GRADIENT_WORDS = [4]; // index 4 "Work." gets gradient color

// ─── Particle config
const PARTICLE_OPTIONS = {
  fullScreen: false,
  background: { color: { value: 'transparent' } },
  fpsLimit: 0,
  interactivity: {
    events: { onHover: { enable: true, mode: 'repulse' }, resize: true },
    modes:  { repulse: { distance: 80, duration: 0.4 } },
  },
  particles: {
    color: { value: ['#C1440E', '#E5622D', '#F5A623'] },
    links: {
      color: '#C1440E', distance: 140, enable: true,
      opacity: 0.12, width: 1,
    },
    move: { enable: true, speed: 0.6, direction: 'none', random: true, straight: false },
    number: { density: { enable: true, area: 900 }, value: 55 },
    opacity: { value: { min: 0.05, max: 0.35 } },
    shape: { type: 'circle' },
    size:  { value: { min: 1, max: 2.5 } },
  },
  detectRetina: true,
};

// ─────────────────────────────────────────────
// HeroSection Component
// ─────────────────────────────────────────────
export default function HeroSection({ onGetStarted, onSeeDemo, onBookConsultant }) {
  const [particlesInit, setParticlesInit] = useState(false);
  const navigate = useNavigate();

  const primaryMagnetic = useMagneticEffect(0.25);
  const secondaryMagnetic = useMagneticEffect(0.2);

  const particlesInitFn = useCallback(async (engine) => {
    await loadSlim(engine);
    setParticlesInit(true);
  }, []);

  return (
    <section style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      background: 'var(--bg-page)',
    }}>

      {/* ── Layer 1: Gradient mesh background ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
      }}>
        {/* Top-left indigo orb */}
        <div style={{
          position: 'absolute', top: '-20%', left: '-10%',
          width: 700, height: 700, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(193, 68, 14,0.18) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }} />
        {/* Bottom-right violet orb */}
        <div style={{
          position: 'absolute', bottom: '-10%', right: '-5%',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(229, 98, 45,0.14) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }} />
        {/* Top-right cyan accent */}
        <div style={{
          position: 'absolute', top: '10%', right: '20%',
          width: 300, height: 300, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245, 166, 35,0.10) 0%, transparent 70%)',
          filter: 'blur(30px)',
        }} />
      </div>

      {/* ── Layer 2: tsParticles ── */}
      {particlesInit && (
        <Particles
          id="hero-particles"
          options={PARTICLE_OPTIONS}
          style={{
            position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          }}
        />
      )}
      {/* Init trigger (renders nothing visible) */}
      <Particles id="init-trigger" init={particlesInitFn} style={{ display: 'none' }} />

      {/* ── Layer 3: Spline 3D Scene ── */}
      <div className="hero-spline-container" style={{
        position: 'absolute', top: 0, right: 0,
        width: '55%', height: '100%', zIndex: 2, opacity: 0.85,
        pointerEvents: 'none',
      }}>
        {/* Self-contained animated placeholder (no external Spline dependency) */}
        <SplinePlaceholder />
        {/* Fade left edge — adapts to theme background */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, var(--bg-page) 0%, transparent 55%)',
          pointerEvents: 'none',
        }} />
      </div>

      {/* ── Layer 4: Main content ── */}
      <div className="hero-container-grid">

        {/* ─ Left: Text block ─ */}
        <div>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: DURATION.normal, ease: EASE.spring, delay: 0.1 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 16px', borderRadius: 100,
              background: 'rgba(193, 68, 14,0.10)',
              border: '1px solid rgba(193, 68, 14,0.25)',
              marginBottom: 24,
            }}
          >
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: '#C1440E',
              boxShadow: '0 0 8px #C1440E',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
            <span style={{
              color: 'var(--text-muted)', fontSize: 13, fontFamily: 'DM Sans, sans-serif',
              letterSpacing: '0.02em',
            }}>
              AI-Powered Startup Intelligence
            </span>
          </motion.div>

          {/* Animated headline */}
          <h1 style={{
            fontSize: 'clamp(42px, 5.5vw, 76px)',
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: '-0.03em',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            margin: '0 0 24px',
          }}>
            {HEADLINE.map((word, i) => (
              <motion.span
                key={word}
                initial={{ opacity: 0, y: 28, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{
                  duration: DURATION.slow,
                  ease: EASE.spring,
                  delay: 0.2 + i * STAGGER.normal,
                }}
                style={{
                  display: 'inline-block',
                  marginRight: '0.22em',
                  color: GRADIENT_WORDS.includes(i) ? 'transparent' : 'var(--text-primary)',
                  backgroundImage: GRADIENT_WORDS.includes(i)
                    ? 'linear-gradient(135deg, #C1440E 0%, #E5622D 55%, #F5A623 100%)'
                    : 'none',
                  backgroundClip: GRADIENT_WORDS.includes(i) ? 'text' : 'unset',
                  WebkitBackgroundClip: GRADIENT_WORDS.includes(i) ? 'text' : 'unset',
                }}
              >
                {word}
              </motion.span>
            ))}
          </h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: DURATION.normal, ease: EASE.spring, delay: 0.65 }}
            style={{
              color: 'var(--text-body)',
              fontSize: 'clamp(16px, 1.8vw, 20px)',
              lineHeight: 1.65,
              marginBottom: 40,
              maxWidth: 520,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            Validate ideas, map markets, connect with mentors, and access cutting-edge
            technology — all in one AI-powered workspace.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: DURATION.normal, ease: EASE.spring, delay: 0.80 }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}
          >
            {/* Primary CTA */}
            <motion.button
              ref={primaryMagnetic.ref}
              {...primaryMagnetic.motionProps}
              data-cursor="cta"
              onClick={onGetStarted || (() => navigate('/signup'))}
              whileHover={{
                scale: 1.05,
                boxShadow: '0 0 40px rgba(193, 68, 14,0.55), 0 8px 32px rgba(193, 68, 14,0.25)',
              }}
              whileTap={{ scale: 0.97 }}
              transition={SPRING.bouncy}
              style={{
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #C1440E 0%, #E5622D 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              Get Started Free
              <span style={{ fontSize: 18 }}>→</span>
            </motion.button>

            {/* Secondary CTA */}
            <motion.button
              ref={secondaryMagnetic.ref}
              {...secondaryMagnetic.motionProps}
              onClick={onSeeDemo}
              whileHover={{
                scale: 1.03,
                borderColor: 'rgba(193, 68, 14,0.50)',
                background: 'var(--glass-bg-hover)',
              }}
              whileTap={{ scale: 0.97 }}
              transition={SPRING.gentle}
              style={{
                padding: '14px 32px',
                background: 'var(--glass-bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--glass-border)',
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                fontFamily: 'DM Sans, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              ▶ See Demo
            </motion.button>
          </motion.div>

          {/* Social proof strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: DURATION.slow }}
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              marginTop: 40, color: 'var(--text-muted)', fontSize: 13,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            <div style={{ display: 'flex' }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: `hsl(${240 + i * 30}, 60%, 55%)`,
                  border: '2px solid var(--border-default)',
                  marginLeft: i > 0 ? -8 : 0,
                }} />
              ))}
            </div>
            <span><strong style={{ color: 'var(--text-primary)' }}>2,400+</strong> founders trust MarsMate</span>
          </motion.div>
        </div>

        {/* ─ Right: Lottie / AI visual panel ─ */}
        <motion.div
          initial={{ opacity: 0, x: 40, scale: 0.92 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: DURATION.slow, ease: EASE.spring, delay: 0.4 }}
          style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--glass-border)',
            borderRadius: 24,
            padding: 32,
            textAlign: 'center',
            boxShadow: 'var(--shadow-lg), 0 0 40px rgba(193, 68, 14, 0.05)',
          }}
        >
          {/* Branded AI orb */}
          <div style={{
            width: 200, height: 200, margin: '0 auto', position: 'relative',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {/* pulsing rings */}
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay: i * 0.8 }}
                style={{
                  position: 'absolute', width: 120, height: 120, borderRadius: '50%',
                  border: '1.5px solid rgba(193,68,14,0.55)',
                }}
              />
            ))}
            {/* core orb */}
            <motion.div
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 110, height: 110, borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 30%, #F5A623 0%, #E5622D 45%, #C1440E 100%)',
                boxShadow: '0 0 40px rgba(193,68,14,0.55), inset 0 2px 12px rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 40,
              }}
            >
              🚀
            </motion.div>
          </div>
          <p style={{
            color: 'var(--text-primary)', fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 700, fontSize: 18, marginBottom: 8,
          }}>
            AI is analyzing your idea
          </p>
          <p style={{
            color: 'var(--text-muted)', fontFamily: 'DM Sans, sans-serif', fontSize: 14, margin: 0,
          }}>
            Real-time market intelligence, validation scores, and competitor radar
          </p>

          {/* Mini stat strip */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 24,
          }}>
            {[
              { label: 'Ideas Validated', value: '12.4K' },
              { label: 'Avg Score', value: '87%' },
            ].map(stat => (
              <div key={stat.label} style={{
                background: 'rgba(193, 68, 14,0.05)',
                borderRadius: 10, padding: '12px 16px',
                border: '1px solid var(--border-default)',
              }}>
                <div style={{
                  fontSize: 22, fontWeight: 800,
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  background: 'linear-gradient(135deg, #C1440E, #F5A623)',
                  backgroundClip: 'text', WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}>
                  {stat.value}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>


      {/* ── Scroll indicator ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: DURATION.slow }}
        style={{
          position: 'absolute', bottom: 32, left: '50%',
          transform: 'translateX(-50%)', zIndex: 10,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 11, letterSpacing: '0.15em', fontFamily: 'DM Sans, sans-serif' }}>
          SCROLL
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 22, height: 36, borderRadius: 11,
            border: '1.5px solid rgba(193, 68, 14,0.35)',
            display: 'flex', justifyContent: 'center', padding: '6px 0',
          }}
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              width: 4, height: 4, borderRadius: '50%',
              background: '#C1440E',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Responsive overrides & animations */}
      <style>{`
        .hero-container-grid {
          position: relative;
          z-index: 10;
          max-width: 1200px;
          margin: 0 auto;
          padding: 120px 32px 48px; /* Offset fixed navbar height */
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 48px;
          align-items: center;
          width: 100%;
        }
        .hero-action-buttons-wrap {
          position: absolute;
          bottom: 32px;
          left: 48px;
          z-index: 100;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        @media (max-width: 1024px) {
          .hero-container-grid {
            grid-template-columns: 1fr !important;
            text-align: center;
            gap: 40px;
            padding-top: 100px;
          }
          .hero-container-grid > div:first-child {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .hero-spline-container {
            display: none !important;
          }
        }
        @media (max-width: 768px) {
          .hero-action-buttons-wrap {
            position: static !important;
            margin-top: 32px !important;
            justify-content: center !important;
            width: 100% !important;
            padding-bottom: 24px;
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </section>
  );
}

function SplinePlaceholder() {
  return (
    <div style={{
      width: '100%', height: '100%',
      background: 'radial-gradient(circle at center, rgba(193, 68, 14,0.08) 0%, transparent 70%)',
    }} />
  );
}
